import type { Action, ProjectChange } from '../pipeline/update-engine.models';
import { getAffectedElementIds } from '../actions/action.helpers';
import { EMPTY_FROZEN_ARRAY } from '../array.helpers';
import { cacheFactory } from '../function.helpers';
import { getId } from '../object.helpers';
import { forEachElement } from '../recursive-element.helpers';
import type { Baseline, OneOfElements } from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';
import type { ItemOrItemId } from '@shared/models/type-helpers.model';

type ProposalInput = Baseline | IProposal[] | undefined;

/**
 * Per-`actions` ref WeakMap: `id → Action`. Avoids rebuilding the lookup every
 * call to {@link isElementTargetedByProposal}, which is invoked O(rows × proposals)
 * by element-list and product-list hooks.
 */
const actionsByIdCache = new WeakMap<readonly Action[], Map<string, Action>>();

const getActionsById = (actions: readonly Action[]): Map<string, Action> => {
  const cached = actionsByIdCache.get(actions);
  if (cached) return cached;
  const map = new Map<string, Action>(actions.map((a) => [a.id, a]));
  actionsByIdCache.set(actions, map);
  return map;
};

/**
 * Per-`Action.changes` ref WeakMap: cached list of element ids each action touches.
 * `getAffectedElementIds` itself walks the changes array, so caching its output
 * by changes-ref keeps the hot per-row check pure lookup.
 *
 * The returned array is shared across every caller for the same `changes` ref —
 * treat it as read-only. Mutating it would poison the cache for all subsequent
 * lookups (and break correctness across unrelated callers).
 */
const actionAffectedElementsCache = new WeakMap<
  Action['changes'],
  readonly string[]
>();

const getCachedAffectedElementIds = (
  changes: Action['changes'],
): readonly string[] => {
  const cached = actionAffectedElementsCache.get(changes);
  if (cached) return cached;
  const ids = getAffectedElementIds(changes);
  actionAffectedElementsCache.set(changes, ids);
  return ids;
};

/**
 * True when some action in {@link Baseline} (via `actions`) linked from
 * {@link IProposal} (via `actionIds`) contains a change whose payload includes
 * {@link elementId} (see {@link getAffectedElementIds}).
 *
 * Both the `id → Action` lookup and each action's affected-elements list are
 * memoized by reference, so calling this for every (proposal × element) pair in
 * a long list is cheap once the version is stable.
 */
export const isElementTargetedByProposal = (
  version: Baseline | undefined,
  proposal: IProposal,
  elementId: string,
): boolean => {
  const actions = version?.actions;
  if (!actions?.length || proposal.actionIds.length === 0) return false;

  const actionById = getActionsById(actions);
  for (const actionId of proposal.actionIds) {
    const action = actionById.get(actionId);
    if (!action) continue;
    if (getCachedAffectedElementIds(action.changes).includes(elementId)) {
      return true;
    }
  }
  return false;
};

/**
 * Flatten changes from actions referenced by {@link proposal} in
 * {@link IProposal.actionIds} order (not {@link Baseline.actions} array order).
 */
export const collectProposalChangesInOrder = (
  version: Baseline,
  proposal: IProposal,
): ProjectChange[] => {
  const actions = version.actions;
  if (!actions?.length) return [];
  const actionsById = getActionsById(actions);
  const ordered: ProjectChange[] = [];
  for (const actionId of proposal.actionIds) {
    const action = actionsById.get(actionId);
    if (!action) continue;
    ordered.push(...action.changes);
  }
  return ordered;
};

/**
 * Get all ids of elements affected by the proposal.
 *
 * - A parent being affected propagates down: all its children are also included.
 * - When `includeParentIds` is `true` the propagation also runs upward: if any
 *   descendant is affected, every ancestor up to the version root is included.
 *   Use this in list rows so parent (and grandparent) elements pick up the
 *   proposal colour when a child element is targeted.
 */
export const getAffectedElementsIdsByProposal = (
  version: Baseline | undefined,
  proposal: IProposal,
  includeParentIds = false,
): Set<string> => {
  // Walking the tree is expensive, so results are memoized per (version, proposal, mode).
  return cacheFactory(
    () => {
      if (includeParentIds) {
        // Reuse the base set (direct targets + descendants), which is cached separately.
        const baseIds = getAffectedElementsIdsByProposal(
          version,
          proposal,
          false,
        );
        // Copy so we don't mutate the cached base set.
        const ids = new Set(baseIds);
        // Second pass: for every affected element, also mark all its ancestors.
        forEachElement(version, (element, path) => {
          if (baseIds.has(element.id)) {
            for (const ancestor of path) {
              ids.add(ancestor.id);
            }
          }
        });
        return ids;
      }

      const ids = new Set<string>();
      forEachElement(version, (element, path) => {
        // Propagate downward: if a parent is affected, all children are too.
        if (path.some(({ id }) => ids.has(id))) {
          ids.add(element.id);
        }
        if (isElementTargetedByProposal(version, proposal, element.id)) {
          ids.add(element.id);
        }
      });
      return ids;
    },
    `getAffectedElementsIdsByProposal[${version?.id}, ${proposal.id}, ${includeParentIds}]`,
    [version, proposal, includeParentIds],
  );
};

/**
 * Get the proposals from a version or an array of proposals.
 * Note: Will return an empty array if the Proposals are not defined.
 *
 * @param versionOrProposals - Building version, a proposals array, or undefined
 */
export const getProposals = (
  versionOrProposals: ProposalInput,
): IProposal[] => {
  if (Array.isArray(versionOrProposals)) {
    return versionOrProposals;
  }
  return versionOrProposals?.proposals ?? EMPTY_FROZEN_ARRAY;
};

/**
 * Get a proposal from a version or an array of proposals by id.
 * @param versionOrProposals - Building version, a proposals array, or undefined
 * @param proposalOrId - Proposal id or object
 * @returns Proposal or undefined
 */
export const getProposal = (
  versionOrProposals: ProposalInput,
  proposalOrId: ItemOrItemId<IProposal> | undefined,
): IProposal | undefined => {
  const id = proposalOrId ? getId(proposalOrId) : undefined;
  return getProposals(versionOrProposals).find((p) => p.id === id);
};

/**
 * Get the proposals from a version that have actions referencing the element.
 * (see {@link isElementTargetedByProposal}).
 */
export const filterProposalsByTargetedElement = (
  version: Baseline | undefined,
  elementOrId: ItemOrItemId<OneOfElements>,
): IProposal[] => {
  const elementId = getId(elementOrId);
  return getProposals(version).filter((p) =>
    isElementTargetedByProposal(version, p, elementId),
  );
};

export const isProposal = (proposal: IProposal | IProposal): boolean => {
  return 'actionIds' in proposal;
};
